/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode } from 'react';
import type { SpecificationRow } from '@/lib/product/specifications';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Joined at render, never in `computeSpecifications` — real spec values contain commas
 * (`10-24t:10,12,14,16,18,21,24` is ONE value), so joining earlier makes value boundaries
 * unrecoverable. It is also English punctuation, which the data layer has no business owning.
 */
const VALUE_SEPARATOR = ', ';

/**
 * Row geometry, per Figma `13588:36938` (desktop) and `13869:82363` (mobile).
 *
 * `min-h-10` is the design's 40px row as a MINIMUM, not a fixed height: the design's own
 * `Weight Based On` row is 64px desktop / 88px mobile where the value wraps. `p-2` produces that
 * 40px from the type alone (8 + 24 + 8) and is load-bearing for the wrapped row too — 88 is exactly
 * 3 × 24 + 16. The 8px is on all four sides, so the label sits 32px inside the accordion's own 24px
 * padding rather than flush against it.
 *
 * The hairline is an inset shadow rather than a border because a border would occupy a pixel, making
 * rows 41px while skeleton rows stayed 40. Figma's arithmetic leaves no room for it either:
 * 88 = 72 + 16.
 *
 * It sits at the BOTTOM, and the last row has none — matching the frame, where every `Detail Row`
 * draws `border-b` except the final one. Drawn at the top instead, the list gained a rule directly
 * under the accordion header that the design does not have.
 *
 * `[0_0_#0000]` and never `shadow-none`: Tailwind composes ring and shadow into one `box-shadow`
 * list, where `none` is illegal as a single item and silently voids the whole declaration.
 *
 * `items-start`, not centred. It only shows on a wrapped row, and there the frame keeps the label on
 * the value's FIRST line; centring floats it halfway down a three-line value.
 */
const ROW_CLASSES =
    'min-h-10 items-start gap-x-1 p-2 shadow-[inset_0_-1px_0_0_var(--color-border-subtle)] last:shadow-[0_0_#0000]';

/**
 * The two columns are EQUAL halves, per the frame's `flex-[1_0_0]` on both the label and the value —
 * not a content-sized label against a filling value. It is what decides where a long value wraps, and
 * therefore the row's height: `Weight Based On` is drawn at three lines on mobile, which only happens
 * when the value is confined to half the width.
 *
 * `minmax(0, …)` because a bare `1fr` floors at min-content, so one unbreakable value would quietly
 * take more than its half — the same guard Figma writes as `min-width: 1px` on both children.
 */
const COLUMN_CLASSES = 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]';

/**
 * Desktop height cap, declared as `max-height: 300px` in Figma `13588:36938`; the mobile node
 * declares none and renders every row, so the constraint is deliberately responsive. `lg:` is where
 * `ProductView` switches to the two-column layout that frame belongs to.
 *
 * 300px is kept literal. It is 7.5 rows, and landing MID-ROW is what makes the cut-off row the
 * scroll affordance — the design draws no scrollbar.
 *
 * `focus-visible:outline-solid` looks redundant and is not. In Tailwind v4 `outline-none` sets
 * `--tw-outline-style: none` and `outline-2` resolves its style from that variable, so the pair
 * computes to `outline-style: none; outline-width: 0px` while colour and offset still apply — an
 * invisible ring that reads as correct in every class string.
 */
const SCROLL_CLASSES =
    'lg:max-h-[300px] lg:overflow-y-auto outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2';

const FALLBACK_ROW_COUNT = 6;

interface ProductSpecificationsProps {
    /** Already unioned, suppressed and ordered by `computeSpecifications`. */
    rows: SpecificationRow[];
    /**
     * Accessible name for the scroll region. REQUIRED: the region is focusable unconditionally, so an
     * omitted name ships a tab stop that announces nothing. Pass the panel's translated heading.
     */
    label: string;
    className?: string;
}

/**
 * Renders the specification rows as a description list.
 *
 * `<dl>` rather than a table: name/value pairs, not tabular data with meaningful columns.
 *
 * Returns `null` rather than an empty list when every attribute is suppressed. Callers are expected to
 * suppress the surrounding section too — `ProductSpecificationsSection` in `product-view.tsx` does — so
 * this is the inner guard rather than the whole story: it keeps a direct consumer from rendering an
 * empty `<dl>`, and costs nothing when the caller already gated.
 */
export default function ProductSpecifications({ rows, label, className }: ProductSpecificationsProps): ReactNode {
    if (!rows.length) return null;

    return (
        <dl
            // Focusable so the desktop scroll region is keyboard-operable; it stays focusable below
            // `lg` where nothing scrolls, because React cannot evaluate a media query and an inert tab
            // stop is cheaper than unreachable content.
            tabIndex={0}
            aria-label={label}
            // Queried by test id, not by role: `<dl>` exposes no `group` role, and forcing one would
            // trade the `dt`/`dd` term/definition mapping for a convenient selector.
            data-testid="product-specifications-list"
            className={cn('grid text-md', COLUMN_CLASSES, SCROLL_CLASSES, className)}>
            {rows.map((row) => (
                <div key={row.attributeId} className={cn('col-span-2 grid grid-cols-subgrid', ROW_CLASSES)}>
                    <dt className="text-text-tertiary">{row.label}</dt>
                    <dd className="text-right text-foreground">{row.values.join(VALUE_SEPARATOR)}</dd>
                </div>
            ))}
        </dl>
    );
}

/**
 * Loading state that RESERVES height rather than rendering nothing — the accordion is expanded by
 * default, so the panel is above the fold and a `null` fallback would shift the page when rows
 * arrive. The row count is a guess; it cannot be exact, since the row count is what is loading.
 */
export function ProductSpecificationsFallback({ className }: { className?: string }): ReactElement {
    return (
        // Capped and scrollable like the real panel. NOT focusable: six skeleton rows never overflow
        // 300px, and a tab stop that vanishes when data arrives is worse than none.
        <div
            className={cn('grid lg:max-h-[300px] lg:overflow-y-auto', COLUMN_CLASSES, className)}
            data-testid="product-specifications-fallback">
            {Array.from({ length: FALLBACK_ROW_COUNT }, (_, index) => (
                // Same ROW_CLASSES as a real row, so the reserved space is the space taken.
                <div key={index} className={cn('col-span-2 grid grid-cols-subgrid', ROW_CLASSES)}>
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-20 justify-self-end" />
                </div>
            ))}
        </div>
    );
}
